import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EEnumPopupPicker from '@/components/everridge/EEnumPopupPicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import EDatePicker from '@/components/everridge/EDatePicker';
import {
	NotificationCategory,
	NotificationType,
} from '@/features/notifications/types/notifications-dto';
import { useNotificationsFilter } from '@/features/notifications/hooks/useNotificationsFilter';
import { NotificationsPageFilterFormSchema } from '@/features/notifications/validators/NotificationsPageFilterValidator';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

type FormData = z.infer<typeof NotificationsPageFilterFormSchema>;

type Props = {
	onClose: () => void;
};

const NotificationsPageFilter: FC<Props> = ({ onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const userType = useUserRole();
	const { t } = useTranslation();
	const { filter, setFilter } = useNotificationsFilter();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(NotificationsPageFilterFormSchema),
		values: {
			startDate: filter.startDate,
			endDate: filter.endDate,
			type: filter.type,
			category: filter.category,
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleSubmitFilterForm = (form: FormData) => {
		setFilter(form);
		handleClose();
	};

	const handleResetFilter = () => {
		reset({
			startDate: null,
			endDate: null,
			type: null,
			category: null,
		});
	};

	const types = Object.values(NotificationType).filter(
		(value) => typeof value === 'string',
	);

	const categories = Object.values(NotificationCategory).filter(
		(value) => typeof value === 'string',
	);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('elements.components.filter.title')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<div className='flex gap-4'>
						<EDatePicker
							control={control}
							fieldName={'startDate'}
							label={t('elements.form.startDate.label')}
							placeholder={t(
								'elements.form.startDate.placeholder',
							)}
						/>
						<EDatePicker
							control={control}
							fieldName={'endDate'}
							label={t('elements.form.endDate.label')}
							placeholder={t('elements.form.endDate.placeholder')}
						/>
					</div>
					{userType === Roles.admin && (
						<EEnumPopupPicker
							control={control}
							fieldName={'type'}
							label={t('elements.form.notificationType.label')}
							placeholder={t(
								'elements.form.notificationType.placeholder',
							)}
							list={types}
						/>
					)}
					{userType === Roles.owner && (
						<EEnumPopupPicker
							control={control}
							fieldName={'category'}
							label={t(
								'elements.form.notificationCategory.label',
							)}
							placeholder={t(
								'elements.form.notificationCategory.placeholder',
							)}
							list={categories}
							variant='type2'
						/>
					)}
				</div>
				<DialogFooter
					className={cn(
						'justify-between',
						isMobile && 'justify-end mt-auto',
					)}>
					<Button
						variant='outline'
						onClick={handleResetFilter}>
						{t('elements.buttons.reset')}
					</Button>
					<Button onClick={handleSubmit(handleSubmitFilterForm)}>
						{t('elements.buttons.apply')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default NotificationsPageFilter;
